import { Box, BoxProps, keyframes } from '@chakra-ui/react';

import { neutral, radius } from 'styles/revampTokens';

/**
 * Loading placeholders for the revamp modules.
 *
 * Self-contained on purpose. The app's older `.shimmerBG` class is declared
 * *nested inside* `.card` in `FullScreenLoader/BoxShimmer.scss`, so it only
 * animates under an ancestor carrying that class — callers that used the class
 * on its own rendered transparent boxes and showed nothing at all. The
 * animation here travels with the component instead.
 *
 * The sweep stays inside the neutral ramp: a placeholder is chrome, not an
 * interactive element, so it never carries the accent.
 */
const shimmer = keyframes({
  '0%': { backgroundPosition: '-1200px 0' },
  '100%': { backgroundPosition: '1200px 0' },
});

const SHIMMER_BASE = '#EDEFF3';

export interface ShimmerProperties extends BoxProps {
  /** Rounded like a control (8px) rather than a hairline bar. */
  control?: boolean;
}

export const Shimmer = ({ control, ...rest }: ShimmerProperties) => (
  <Box
    aria-hidden
    bg={SHIMMER_BASE}
    bgImage={`linear-gradient(90deg, ${SHIMMER_BASE} 8%, ${neutral.canvas} 18%, ${SHIMMER_BASE} 33%)`}
    bgSize='1200px 100%'
    animation={`${shimmer} 2.2s linear infinite`}
    borderRadius={control ? radius.control : '4px'}
    // Honour the OS setting: a sweeping gradient is decorative, and leaving it
    // running is an accessibility problem for people who ask for less motion.
    sx={{ '@media (prefers-reduced-motion: reduce)': { animation: 'none' } }}
    {...rest}
  />
);

export interface ShimmerTextProperties extends BoxProps {
  /** Number of lines to draw. */
  lines?: number;
  /** Height of each line. */
  lineHeight?: string;
  /** Width of the final line, so the block reads as prose rather than a slab. */
  lastLineWidth?: string;
}

export const ShimmerText = ({
  lines = 3,
  lineHeight = '10px',
  lastLineWidth = '60%',
  ...rest
}: ShimmerTextProperties) => (
  <Box display='flex' flexDirection='column' gap='8px' width='100%' {...rest}>
    {Array.from({ length: lines }).map((_, index) => (
      <Shimmer
        // eslint-disable-next-line react/no-array-index-key
        key={index}
        height={lineHeight}
        width={index === lines - 1 ? lastLineWidth : '100%'}
      />
    ))}
  </Box>
);

export default Shimmer;
